import {
  Component,
  type ErrorInfo,
  type ReactNode,
} from 'react'
import {
  captureFrontendException,
} from '../analytics/posthog'

interface ErrorBoundaryProps {
  children: ReactNode
  resetKey?: string
  boundary: 'page' | 'root'
}

interface ErrorBoundaryState {
  hasError: boolean
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureFrontendException(error, {
      component_stack: info.componentStack,
      boundary: this.props.boundary,
    })
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <div role="alert" className="min-h-[50vh] flex flex-col items-center justify-center gap-4 text-center px-4">
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong on this page.</h1>
        <p className="text-gray-600">Reloading usually sorts it out.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Reload
        </button>
      </div>
    )
  }
}
